import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthSession, clientApi, bookingApi } from '../services/api';
import Mascot from '../components/Mascot';
import DashboardSidebar from '../components/DashboardSidebar';
import ClientBlogsSection from '../components/ClientBlogsSection';
import { buildWhatsAppLink } from '../utils/whatsapp';
import InbuiltWhatsAppModal from '../components/InbuiltWhatsAppModal';
import BookingActionModal from '../components/BookingActionModal';

// Example bookings for demo
const exampleBookings = [
    {
      _id: 'b1',
      therapist: { firstName: 'Rajesh', lastName: 'Sharma', specializations: ['Anxiety & Depression'] },
      sessionDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(),
      startTime: '10:00 AM',
      endTime: '11:00 AM',
      sessionType: 'online',
      status: 'confirmed',
      meetingLink: 'https://meet.google.com/abc-defg-hij'
    },
    {
      _id: 'b2',
      therapist: { firstName: 'Priya', lastName: 'Patel', specializations: ['Relationship Counseling'] },
      sessionDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
      startTime: '2:00 PM',
      endTime: '3:00 PM',
      sessionType: 'in-person',
      status: 'confirmed'
    },
    {
      _id: 'b3',
      therapist: { firstName: 'Amit', lastName: 'Verma', specializations: ['Career Counseling'] },
      sessionDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
      startTime: '11:30 AM',
      endTime: '12:30 PM',
      sessionType: 'online',
      status: 'pending'
    }
  ];

const ClientDashboard = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [whatsappTarget, setWhatsappTarget] = useState(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionModalBooking, setActionModalBooking] = useState(null);
  const [actionModalType, setActionModalType] = useState('reschedule');

  useEffect(() => {
    if (!authSession?.token) {
      navigate('/login');
      return;
    }
    
    setUserName(authSession.user?.name || 'Friend');

    const fetchBookings = async () => {
      setLoading(true);
      try {
        const response = await bookingApi.mine();
        const raw = response.bookings || response.data || response || [];
        if (Array.isArray(raw)) {
          setBookings(raw);
        }
      } catch (err) {
        console.error('Failed to load bookings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [authSession?.token, navigate]);

  return (
    <>
      <DashboardSidebar userRole="client" />
      <div className="dashboard-page-content" style={{ marginLeft: '280px' }}>
      {/* Mascot Welcome Widget */}
      <div style={{ marginBottom: '20px' }}>
        <Mascot userName={userName || 'Friend'} />
      </div>

      {/* Welcome Banner */}
      <div className="welcome-banner client-banner">
        <div className="welcome-content">
          <h2>Welcome back, {userName}! 👋</h2>
          <p>Your mental health journey continues here. Book sessions, track progress, and explore resources.</p>
        </div>
        <Link to="/appointment" className="welcome-cta">
          🔍 Find a Therapist
        </Link>
      </div>

      {/* Quick Action Cards */}
      <div className="client-quick-actions">
        <Link to="/client/profile" className="client-action-card">
          <div className="action-icon purple">👤</div>
          <div className="action-info">
            <h4>My Profile</h4>
            <p>Goals & preferences</p>
          </div>
        </Link>
        <Link to="/client/worksheets" className="client-action-card">
          <div className="action-icon purple">📑</div>
          <div className="action-info">
            <h4>My Worksheets</h4>
            <p>Live sheets & exercises</p>
          </div>
        </Link>
        <Link to="/client/bookings" className="client-action-card">
          <div className="action-icon blue">📅</div>
          <div className="action-info">
            <h4>My Bookings</h4>
            <p>View all appointments</p>
          </div>
        </Link>
        <Link to="/therapists" className="client-action-card">
          <div className="action-icon pink">👨‍⚕️</div>
          <div className="action-info">
            <h4>Find Therapist</h4>
            <p>Browse verified therapists</p>
          </div>
        </Link>
        <Link to="/self-help" className="client-action-card">
          <div className="action-icon green">🧘</div>
          <div className="action-info">
            <h4>Self-Help</h4>
            <p>Meditation & resources</p>
          </div>
        </Link>
      </div>

      {/* Two Column Layout */}
      <div className="dashboard-two-cols">
        {/* Upcoming Sessions */}
        <div className="dashboard-section-card">
          <div className="section-header">
            <h3>📆 Upcoming Sessions</h3>
            <Link to="/client/bookings" className="view-all-link">View All</Link>
          </div>

          {loading ? (
            <div className="section-loading">
              <div className="spinner"></div>
              <p>Loading sessions...</p>
            </div>
          ) : bookings.length > 0 ? (
            <div className="sessions-list">
              {bookings.slice(0, 5).map((booking) => {
                const scheduledDate = new Date(booking.scheduledAt || booking.sessionDate || booking.date || Date.now());
                const tp = booking.physiotherapistProfileId || booking.therapistProfile || {};
                const tu = tp.userId || {};
                const therapistName = tu.name || tp.name || (booking.therapist ? `Dr. ${booking.therapist.firstName || ''} ${booking.therapist.lastName || ''}`.trim() : 'Therapist');
                const therapistPhone = tu.phone || tp.phone || booking.therapistPhone || '';
                const therapistEmail = tu.email || tp.email || booking.therapistEmail || '';
                const therapistUserId = tu._id || tu.id || tp._id || tp.id || booking.physiotherapistProfileId;
                const sessionMode = booking.mode || booking.sessionType || 'online';
                const timeString = scheduledDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

                return (
                  <div key={booking._id} className="session-item">
                    <div className="session-date-box">
                      <span className="date-day">{scheduledDate.getDate()}</span>
                      <span className="date-month">{scheduledDate.toLocaleString('default', { month: 'short' })}</span>
                    </div>
                    <div className="session-info">
                      <h4>👨‍⚕️ {therapistName}</h4>
                      <p>
                        🕐 {timeString}
                        <span className={`type-badge ${sessionMode}`}>
                          {sessionMode === 'online' ? '💻 Online' : '🏥 In-Person'}
                        </span>
                      </p>
                      {booking.rescheduleReason && (
                        <span style={{ fontSize: '11px', color: '#6d28d9', background: '#f5f3ff', padding: '2px 6px', borderRadius: '4px', marginTop: '4px', display: 'inline-block' }}>
                          🔄 Rescheduled: {booking.rescheduleReason}
                        </span>
                      )}
                    </div>
                    <div className="session-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {sessionMode === 'online' && (booking.googleMeetLink || booking.meetingLink) && (
                        <a href={booking.googleMeetLink || booking.meetingLink} className="join-btn" target="_blank" rel="noopener noreferrer" style={{ background: '#2563eb', color: '#fff', padding: '6px 12px', borderRadius: '16px', fontWeight: 700, fontSize: '0.8rem', textDecoration: 'none' }}>
                          🎥 Google Meet
                        </a>
                      )}

                      <button
                        onClick={() => {
                          setWhatsappTarget({
                            _id: therapistUserId,
                            name: therapistName,
                            phone: therapistPhone,
                            email: therapistEmail,
                            role: 'therapist'
                          });
                          setWhatsappModalOpen(true);
                        }}
                        title="Open Inbuilt WhatsApp Chat"
                        style={{
                          background: '#00a884',
                          color: '#fff',
                          padding: '6px 14px',
                          borderRadius: '16px',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          boxShadow: '0 2px 5px rgba(0,168,132,0.25)',
                          transition: 'transform 0.15s ease'
                        }}
                      >
                        💬 Inbuilt WhatsApp
                      </button>

                      {therapistPhone && (
                        <a
                          href={buildWhatsAppLink(therapistPhone, `Hello ${therapistName}, I am reaching out regarding my therapy session on PsychMitra.`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open in Official WhatsApp App"
                          style={{
                            background: '#25D366',
                            color: '#fff',
                            padding: '6px 12px',
                            borderRadius: '16px',
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            boxShadow: '0 2px 5px rgba(37,211,102,0.3)'
                          }}
                        >
                          📱 WhatsApp App
                        </a>
                      )}

                      {booking.status === 'pending' && (
                        <>
                          <button
                            onClick={() => {
                              setActionModalBooking(booking);
                              setActionModalType('reschedule');
                              setActionModalOpen(true);
                            }}
                            title="Reschedule Appointment"
                            style={{
                              background: '#7c3aed',
                              color: '#fff',
                              padding: '6px 12px',
                              borderRadius: '16px',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              boxShadow: '0 2px 5px rgba(124,58,237,0.25)'
                            }}
                          >
                            🔄 Reschedule
                          </button>

                          <button
                            onClick={() => {
                              setActionModalBooking(booking);
                              setActionModalType('cancel');
                              setActionModalOpen(true);
                            }}
                            title="Cancel Appointment"
                            style={{
                              background: '#fff',
                              color: '#dc2626',
                              padding: '6px 12px',
                              borderRadius: '16px',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              border: '1px solid #fca5a5',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            ✕ Cancel
                          </button>
                        </>
                      )}
                      
                      <span className={`status-badge ${booking.status}`}>{booking.status}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-section">
              <span className="empty-icon">📅</span>
              <h4>No upcoming sessions</h4>
              <p>Book your first session with a therapist</p>
              <Link to="/therapists" className="empty-cta">Find a Therapist</Link>
            </div>
          )}
        </div>

        {/* Resources Section */}
        <div className="dashboard-section-card">
          <div className="section-header">
            <h3>🎯 Explore Resources</h3>
          </div>
          <div className="resources-list">
            <Link to="/events" className="resource-item">
              <div className="resource-icon orange">🎉</div>
              <div className="resource-info">
                <h4>Events & Workshops</h4>
                <p>Join live sessions and webinars</p>
              </div>
            </Link>
            <Link to="/products" className="resource-item">
              <div className="resource-icon green">📚</div>
              <div className="resource-info">
                <h4>E-Learning Store</h4>
                <p>E-books, courses & materials</p>
              </div>
            </Link>
            <Link to="/community" className="resource-item">
              <div className="resource-icon blue">👥</div>
              <div className="resource-info">
                <h4>Community</h4>
                <p>Connect with others</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Blogs Section */}
      <div style={{ marginTop: '30px' }}>
        <ClientBlogsSection />
      </div>

      <style>{`
        .dashboard-page-content {
          padding: 30px;
          background: #f8f9fa;
          min-height: 100vh;
          margin-left: 280px;
        }

        .welcome-banner.client-banner {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 16px;
          padding: 28px;
          color: white;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 20px;
        }
        .welcome-content h2 { margin: 0 0 8px 0; font-size: 24px; }
        .welcome-content p { margin: 0; opacity: 0.9; font-size: 15px; max-width: 400px; }
        .welcome-cta {
          padding: 12px 24px;
          background: white;
          color: #667eea;
          border-radius: 30px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s;
        }
        .welcome-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.2); }

        .client-quick-actions {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }
        .client-action-card {
          background: white;
          border-radius: 14px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          text-decoration: none;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          transition: all 0.3s;
        }
        .client-action-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }
        .action-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }
        .action-icon.purple { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
        .action-icon.blue { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
        .action-icon.pink { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
        .action-icon.green { background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%); }
        .action-info h4 { margin: 0 0 4px 0; color: #333; font-size: 15px; }
        .action-info p { margin: 0; color: #666; font-size: 13px; }

        .dashboard-two-cols { display: grid; grid-template-columns: 1.2fr 1fr; gap: 24px; }

        .dashboard-section-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
        }
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .section-header h3 { margin: 0; font-size: 17px; color: #333; }
        .view-all-link { color: #667eea; text-decoration: none; font-size: 14px; font-weight: 500; }

        .sessions-list { display: flex; flex-direction: column; gap: 14px; }
        .session-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: #f5f7fb;
          border-radius: 12px;
        }
        .session-date-box {
          width: 54px;
          text-align: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 10px 8px;
          border-radius: 10px;
          color: white;
        }
        .date-day { display: block; font-size: 22px; font-weight: 700; line-height: 1; }
        .date-month { display: block; font-size: 11px; text-transform: uppercase; margin-top: 2px; }
        .session-info { flex: 1; }
        .session-info h4 { margin: 0 0 6px 0; font-size: 15px; color: #333; }
        .session-info p { margin: 0; font-size: 13px; color: #666; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .type-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }
        .type-badge.online { background: #dbeafe; color: #2563eb; }
        .type-badge.in-person { background: #d1fae5; color: #059669; }
        .session-actions { display: flex; align-items: center; gap: 10px; }
        .join-btn {
          padding: 8px 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 8px;
          text-decoration: none;
          font-size: 13px;
          font-weight: 600;
        }
        .status-badge {
          padding: 5px 12px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          text-transform: capitalize;
        }
        .status-badge.confirmed { background: #e8f5e9; color: #2e7d32; }
        .status-badge.pending { background: #fff3e0; color: #ef6c00; }

        .resources-list { display: flex; flex-direction: column; gap: 14px; }
        .resource-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: #f5f7fb;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .resource-item:hover { background: #eef1f8; transform: translateX(5px); }
        .resource-icon {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }
        .resource-icon.orange { background: linear-gradient(135deg, #f5af19 0%, #f12711 100%); }
        .resource-info h4 { margin: 0 0 4px 0; font-size: 14px; color: #333; }
        .section-loading, .empty-section { text-align: center; padding: 40px 20px; }
        .empty-icon { font-size: 48px; display: block; margin-bottom: 12px; }
        .empty-section h4 { margin: 0 0 8px 0; color: #333; font-size: 16px; }
        .empty-section p { margin: 0 0 16px 0; color: #666; font-size: 14px; }
        .empty-cta {
          display: inline-block;
          padding: 10px 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 8px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 600;
        }

        @media (max-width: 1024px) {
          .dashboard-two-cols { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .welcome-banner.client-banner { flex-direction: column; text-align: center; }
          .session-item { flex-direction: column; text-align: center; }
          .session-actions { justify-content: center; }
        }
      `}</style>
      <InbuiltWhatsAppModal
        isOpen={whatsappModalOpen}
        onClose={() => setWhatsappModalOpen(false)}
        targetUser={whatsappTarget}
        contactsList={bookings.map(b => {
          const tp = b.physiotherapistProfileId || b.therapistProfile || {};
          const tu = tp.userId || {};
          return {
            _id: tu._id || tu.id || tp._id || tp.id || b.physiotherapistProfileId,
            name: tu.name || tp.name || (b.therapist ? `Dr. ${b.therapist.firstName || ''} ${b.therapist.lastName || ''}`.trim() : 'Therapist'),
            phone: tu.phone || tp.phone || b.therapistPhone || '',
            email: tu.email || tp.email || b.therapistEmail || '',
            role: 'therapist',
            status: b.status,
            isSessionCompleted: b.status === 'completed'
          };
        })}
      />
      <BookingActionModal
        isOpen={actionModalOpen}
        onClose={() => setActionModalOpen(false)}
        booking={actionModalBooking}
        actionType={actionModalType}
        userRole="client"
        onSuccess={async () => {
          try {
            const res = await bookingApi.mine();
            const raw = res.bookings || res.data || res || [];
            if (Array.isArray(raw)) {
              setBookings(raw);
            }
          } catch (e) {
            console.error(e);
          }
        }}
      />
    </div>
    </>
  );
};

export default ClientDashboard;
